import { useMemo } from "react";
import { useCachedQuery } from "./useCachedQuery";
import { ADMIN_QUERIES } from "../lib/adminQueries";
import { getCsrfHeader } from "../lib/csrf";

// Mirrors the backend RateLimit*View DTOs (AdminRateLimitController). One GET,
// no filters, so the URL is constant and always cache-worthy.
export type RateLimitState = "ok" | "alert" | "block";

export type RateLimitWindowLimit = {
  window: string;
  alert: number | null;
  block: number | null;
};

export type RateLimitTenantWindow = {
  window: string;
  observed: number;
  alert: number | null;
  block: number | null;
  state: RateLimitState;
  stateSince: string | null;
};

export type RateLimitTenant = {
  tenantId: string;
  tenantName: string | null;
  ownerEmail: string | null;
  worstState: RateLimitState;
  windows: RateLimitTenantWindow[];
};

// Per-app tier (additive fields on the same GET): configured per-app limits
// (integrationSlug '*' = the fallback default row) and the attention-worthy
// (instance, app) pairs.
export type AppRateLimit = {
  integrationSlug: string;
  windows: RateLimitWindowLimit[];
};

export type AppRateLimitPair = {
  tenantId: string;
  tenantName: string | null;
  ownerEmail: string | null;
  integrationSlug: string;
  worstState: RateLimitState;
  windows: RateLimitTenantWindow[];
};

export type RateLimitDashboard = {
  windows: RateLimitWindowLimit[];
  tenants: RateLimitTenant[];
  apps: AppRateLimit[];
  appPairs: AppRateLimitPair[];
  // Active catalog slugs, for the add-app dropdown.
  catalogSlugs: string[];
};

type FetchStatus = "loading" | "ready" | "error";

type Result = {
  status: FetchStatus;
  data: RateLimitDashboard | null;
  error: string | null;
  isFetching: boolean;
  refresh: () => void;
  // Edit one window's alert/block thresholds (PUT). Throws on a non-2xx.
  updateWindow: (window: string, alert: number | null, block: number | null) => Promise<void>;
  // Edit one app's thresholds for one window (creates the row when absent, so
  // "add app" is a PUT on a fresh slug). Throws on a non-2xx.
  updateAppWindow: (
    slug: string,
    window: string,
    alert: number | null,
    block: number | null,
  ) => Promise<void>;
  // Remove an app's rows entirely; it falls back to the '*' defaults.
  deleteApp: (slug: string) => Promise<void>;
};

export const useAdminRateLimits = (): Result => {
  const url = ADMIN_QUERIES.rateLimits.url;

  const fetcher = useMemo(
    () => async (signal: AbortSignal): Promise<RateLimitDashboard> => {
      const res = await fetch(url, { credentials: "include", signal });
      if (!res.ok) throw new Error(`fetch failed (${res.status})`);
      return (await res.json()) as RateLimitDashboard;
    },
    [url],
  );

  const { status, data, error, isFetching, refetch } = useCachedQuery<RateLimitDashboard>({
    cacheResource: ADMIN_QUERIES.rateLimits.resource,
    queryKey: url,
    fetcher,
  });

  const send = async (path: string, method: "PUT" | "DELETE", body?: unknown): Promise<void> => {
    const res = await fetch(`${url}${path}`, {
      method,
      credentials: "include",
      headers: { "Content-Type": "application/json", ...getCsrfHeader() },
      body: body === undefined ? null : JSON.stringify(body),
    });
    if (!res.ok) {
      const parsed = (await res.json().catch(() => null)) as { message?: string } | null;
      throw new Error(parsed?.message ?? `update failed (${res.status})`);
    }
  };

  const updateWindow = (window: string, alert: number | null, block: number | null): Promise<void> =>
    send(`/windows/${encodeURIComponent(window)}`, "PUT", { alert, block });

  const updateAppWindow = (
    slug: string,
    window: string,
    alert: number | null,
    block: number | null,
  ): Promise<void> =>
    send(`/apps/${encodeURIComponent(slug)}/windows/${encodeURIComponent(window)}`, "PUT", {
      alert,
      block,
    });

  const deleteApp = (slug: string): Promise<void> =>
    send(`/apps/${encodeURIComponent(slug)}`, "DELETE");

  return {
    status,
    data,
    error,
    isFetching,
    refresh: () => void refetch(),
    updateWindow,
    updateAppWindow,
    deleteApp,
  };
};
